import { useEffect, useRef } from 'react';
import type { JSX, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { useOverlayMotion } from '../lib/useOverlayMotion.js';

/**
 * Modal : la pop-up centrale STANDARD des apps MyTaki.
 *
 * Usage impose (doctrine gabarit) : tout ecran d'ajout ou d'edition (ajouter un
 * chat, une ligne, un produit...) s'ouvre dans ce composant, JAMAIS dans la
 * zone centrale du gabarit. L'habillage reprend exactement celui du lanceur
 * rapide : voile translucide floute, panneau blanc arrondi centre.
 *
 * Doctrine : un seul modal ouvert a la fois par ecran (un seul etat openModal
 * par ecran, ex. `useState<'outing' | 'cat' | null>(null)`).
 *
 * Fermeture : clic sur le voile, touche Escape, ou un bouton de l'app dans
 * `footer` qui appelle `onClose`.
 *
 * Motion : ouverture ET fermeture animees d'office (voile en fondu, panneau
 * en pop, utilitaires taki-anim-* du socle). C'est la norme de tout overlay
 * MyTaki : une app n'ecrit JAMAIS d'animation, elle bascule juste `open`.
 */
export interface ModalProps {
  /** Ouvert (rendu) ou non. Rien ne se rend si faux. */
  open: boolean;
  /** Fermeture : clic sur le voile, touche Escape, ou bouton de l'app. */
  onClose: () => void;
  /** Titre du panneau, rendu en tete (h2) et utilise en aria-label. */
  title: string;
  /** Largeur du panneau : sm w-[25rem], md w-[32rem] (defaut), lg w-[40rem]. */
  size?: 'sm' | 'md' | 'lg';
  /** Corps (formulaire). Defile si trop haut. */
  children: ReactNode;
  /** Rangee d'actions optionnelle en pied de panneau (boutons @taki/ui). */
  footer?: ReactNode;
}

const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'w-[25rem]',
  md: 'w-[32rem]',
  lg: 'w-[40rem]',
};

export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  children,
  footer,
}: ModalProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);

  // Cycle de vie anime (norme du socle) : quand `open` passe a faux, le
  // modal reste monte le temps de l'animation de sortie (phase `closing`,
  // classes taki-anim-*-out) puis disparait.
  const { mounted, closing } = useOverlayMotion(open);

  // Touche Escape : ferme le modal tant qu'il est ouvert.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // A l'ouverture : focus sur le panneau pour qu'Escape fonctionne immediatement.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!mounted) return null;

  return (
    // z-30 : SOUS le lanceur rapide du shell (z-40), pour qu'il puisse
    // s'ouvrir par-dessus un modal d'app.
    <div
      className={cn(
        'fixed inset-0 z-30 flex items-center justify-center',
        // Pendant la sortie, l'overlay laisse passer les clics : l'ecran
        // redessous est utilisable sans attendre la fin du fondu.
        closing && 'pointer-events-none',
      )}
    >
      {/* Voile : token translucide dedie (bg-scrim), jamais `bg-bg/xx`. */}
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-scrim backdrop-blur-sm',
          closing ? 'taki-anim-fade-out' : 'taki-anim-fade',
        )}
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'relative z-10 flex max-h-[80%] flex-col outline-none',
          SIZE_CLASSES[size],
          'rounded-2xl border border-border bg-surface p-4 shadow-panel',
          closing ? 'taki-anim-pop-out' : 'taki-anim-pop',
        )}
      >
        <h2 className="shrink-0 pb-3 text-sm font-semibold text-ink-900">{title}</h2>

        {/* Corps : seule partie qui defile si le contenu est trop haut. */}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

        {footer != null && (
          <div className="flex shrink-0 items-center justify-end gap-2 pt-3">{footer}</div>
        )}
      </div>
    </div>
  );
}

export default Modal;
